import {
  addDays,
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  startOfWeek,
} from 'date-fns';

import type {
  ActivityCounts,
  ActivityRange,
  TeamActivityDay,
  TeamActivityMember,
  TeamActivityResponse,
  TeamGoal,
  TeamGoalCadence,
} from '@/api/gamma-team/types';

import { isInactiveMember } from './team-sorting';

export const granularity = { Day: 'day', Week: 'week' } as const;
export type Granularity = (typeof granularity)[keyof typeof granularity];

export const ACTIVITY_KINDS = ['calls', 'messages', 'meetings', 'notes'] as const;
export type ActivityKindKey = (typeof ACTIVITY_KINDS)[number];

export const ACTIVITY_KIND_LABELS: Record<ActivityKindKey, string> = {
  calls: 'Calls',
  messages: 'Messages',
  meetings: 'Meetings',
  notes: 'Notes',
};

export const METRIC_LABELS: Record<keyof ActivityCounts, string> = {
  contactsReached: 'Contacts reached',
  entries: 'Entries',
  calls: 'Calls',
  messages: 'Messages',
  meetings: 'Meetings',
  notes: 'Notes',
  dealsCreated: 'Deals created',
  dealsAdvanced: 'Deals advanced',
};

const ISO_DAY = 'yyyy-MM-dd';
const parseDay = (day: string): Date => parseISO(day);

export interface ActivityBucket extends ActivityCounts {
  key: string;
  label: string;
  dayCount: number;
  dates: string[];
  activeMembers: number | null;
}

const ZERO: ActivityCounts = {
  contactsReached: 0,
  entries: 0,
  calls: 0,
  messages: 0,
  meetings: 0,
  notes: 0,
  dealsCreated: 0,
  dealsAdvanced: 0,
};

const COUNT_KEYS = Object.keys(ZERO) as (keyof ActivityCounts)[];

export const weekKey = (day: string): string =>
  format(startOfWeek(parseDay(day), { weekStartsOn: 1 }), ISO_DAY);

export const formatDayLabel = (day: string): string => {
  const date = parseDay(day);
  return isValid(date) ? format(date, 'MMM d') : day;
};

export const toBuckets = (daily: readonly TeamActivityDay[], by: Granularity): ActivityBucket[] => {
  if (by === granularity.Day) {
    return daily.map((day) => ({
      ...day,
      key: day.date,
      label: formatDayLabel(day.date),
      dayCount: 1,
      dates: [day.date],
      activeMembers: day.activeMembers,
    }));
  }

  const weeks = new Map<string, ActivityBucket>();
  for (const day of daily) {
    const key = weekKey(day.date);
    const bucket = weeks.get(key) ?? {
      ...ZERO,
      key,
      label: `Week of ${formatDayLabel(key)}`,
      dayCount: 0,
      dates: [],
      activeMembers: null,
    };
    for (const metric of COUNT_KEYS) bucket[metric] += day[metric];
    bucket.dayCount += 1;
    bucket.dates.push(day.date);
    weeks.set(key, bucket);
  }
  return [...weeks.values()];
};

export const bucketMemberActivity = (
  daily: readonly TeamActivityDay[],
  values: readonly number[],
  by: Granularity,
): number[] => {
  if (by === granularity.Day) return daily.map((_, index) => values[index] ?? 0);
  const weeks = new Map<string, number>();
  daily.forEach((day, index) => {
    const key = weekKey(day.date);
    weeks.set(key, (weeks.get(key) ?? 0) + (values[index] ?? 0));
  });
  return [...weeks.values()];
};

export const rangeDayCount = (range: ActivityRange): number => {
  const from = parseISO(range.from);
  const to = parseISO(range.to);
  if (!isValid(from) || !isValid(to)) return 0;
  return Math.max(0, Math.round((to.getTime() - from.getTime()) / 86_400_000));
};

export const previousAverage = (
  data: Pick<TeamActivityResponse, 'previous'>,
  metric: keyof ActivityCounts,
  by: Granularity,
): number | null => {
  if (!data.previous) return null;
  const days = rangeDayCount(data.previous.range);
  if (days === 0) return null;
  const perDay = data.previous.total[metric] / days;
  return by === granularity.Week ? perDay * 7 : perDay;
};

const formatSigned = (value: number): string => (value > 0 ? `+${value}` : `${value}`);

export type ChangeTone = 'up' | 'down' | 'flat';

export interface Change {
  label: string;
  tone: ChangeTone;
  description: string;
}

export const compareCounts = (current: number, previous: number, previousLabel: string): Change => {
  const description = `${current.toLocaleString()} vs ${previous.toLocaleString()} in ${previousLabel}`;
  if (current === previous) return { label: 'No change', tone: 'flat', description };
  if (previous === 0) {
    return { label: `${formatSigned(current - previous)} new`, tone: 'up', description };
  }
  const percent = Math.round(((current - previous) / previous) * 100);
  return {
    label: `${formatSigned(percent)}%`,
    tone: percent > 0 ? 'up' : percent < 0 ? 'down' : 'flat',
    description,
  };
};

export const formatPerDay = (total: number, days: number): string | undefined => {
  if (days <= 0 || total === 0) return undefined;
  const perDay = total / days;
  return `~${perDay >= 10 ? Math.round(perDay) : perDay.toFixed(1)}/day`;
};

export const countActiveMembers = (members: readonly TeamActivityMember[]): number =>
  members.filter((member) => !isInactiveMember(member)).length;

export const dealMoves = (counts: Pick<ActivityCounts, 'dealsCreated' | 'dealsAdvanced'>): number =>
  counts.dealsCreated + counts.dealsAdvanced;

export const HEAT_LEVELS = 4;

export const heatLevel = (value: number, max: number): number => {
  if (value <= 0 || max <= 0) return 0;
  return Math.min(HEAT_LEVELS, Math.max(1, Math.ceil((value / max) * HEAT_LEVELS)));
};

export const IDLE_AFTER_DAYS = 7;

export const daysSince = (iso: string | null | undefined, now: Date): number | null => {
  if (!iso) return null;
  const date = parseISO(iso);
  return isValid(date) ? Math.max(0, differenceInCalendarDays(now, date)) : null;
};

export type IdleState =
  | { kind: 'active' }
  | { kind: 'idle'; days: number }
  | { kind: 'never' }
  | { kind: 'unknown' };

export const idleState = (member: TeamActivityMember, now: Date): IdleState => {
  if (member.lastActivityAt === undefined) return { kind: 'unknown' };
  const days = daysSince(member.lastActivityAt, now);
  if (days === null) return { kind: 'never' };
  return days >= IDLE_AFTER_DAYS ? { kind: 'idle', days } : { kind: 'active' };
};

const CADENCE_DAYS: Record<TeamGoalCadence, number> = { weekly: 7, monthly: 30 };

export type GoalStatus = 'met' | 'onTrack' | 'behind';

export interface GoalProgress {
  goal: TeamGoal;
  actual: number;
  expected: number;
  ratio: number;
  status: GoalStatus;
}

export const ON_TRACK_RATIO = 0.8;

export const goalProgress = (
  goal: TeamGoal,
  member: ActivityCounts,
  reportedDays: number,
): GoalProgress => {
  const expected = Math.max(
    1,
    Math.ceil((goal.target * reportedDays) / CADENCE_DAYS[goal.cadence]),
  );
  const actual = member[goal.metric];
  const ratio = actual / expected;
  return {
    goal,
    actual,
    expected,
    ratio,
    status: ratio >= 1 ? 'met' : ratio >= ON_TRACK_RATIO ? 'onTrack' : 'behind',
  };
};

export const addIsoDays = (day: string, amount: number): string =>
  format(addDays(parseDay(day), amount), ISO_DAY);

export const memberView = { Active: 'active', All: 'all', Idle: 'idle' } as const;
export type MemberView = (typeof memberView)[keyof typeof memberView];

export const MEMBER_VIEW_OPTIONS: { value: MemberView; label: string }[] = [
  { value: memberView.Active, label: 'Active in period' },
  { value: memberView.All, label: 'Everyone' },
  { value: memberView.Idle, label: `Idle ${IDLE_AFTER_DAYS}+ days` },
];

const isIdle = (member: TeamActivityMember, now: Date): boolean => {
  const state = idleState(member, now);
  if (state.kind === 'unknown') return isInactiveMember(member);
  return state.kind === 'idle' || state.kind === 'never';
};

export const filterMembersByView = (
  members: readonly TeamActivityMember[],
  view: MemberView,
  now: Date,
): TeamActivityMember[] => {
  if (view === memberView.All) return [...members];
  if (view === memberView.Active) return members.filter((member) => !isInactiveMember(member));
  return members.filter((member) => isIdle(member, now));
};

export const countIdleMembers = (members: readonly TeamActivityMember[], now: Date): number =>
  members.filter((member) => isIdle(member, now)).length;
